export type QueryParamChange =
  | { type: "changed"; key: string; from: string; to: string }
  | { type: "added"; key: string; value: string }
  | { type: "removed"; key: string; value: string };

export type UrlMappingResultDiff = {
  originChanged: boolean;
  pathnameChanged: boolean;
  inputOrigin: string;
  mappedOrigin: string;
  inputPathname: string;
  mappedPathname: string;
  queryChanges: QueryParamChange[];
  isIdentical: boolean;
};

const parseUrlSafe = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

export const diffUrlMappingResult = (
  inputUrl: string,
  mappedUrl: string
): UrlMappingResultDiff | null => {
  const input = parseUrlSafe(inputUrl);
  const mapped = parseUrlSafe(mappedUrl);
  if (!input || !mapped) return null;

  const inputParams = new Map(input.searchParams.entries());
  const mappedParams = new Map(mapped.searchParams.entries());
  const queryChanges: QueryParamChange[] = [];

  const allKeys = new Set([
    ...Array.from(inputParams.keys()),
    ...Array.from(mappedParams.keys()),
  ]);
  allKeys.forEach((key) => {
    const from = inputParams.get(key);
    const to = mappedParams.get(key);
    if (from === to) return;

    if (from === undefined) {
      queryChanges.push({ type: "added", key, value: to ?? "" });
      return;
    }
    if (to === undefined) {
      queryChanges.push({ type: "removed", key, value: from });
      return;
    }
    queryChanges.push({ type: "changed", key, from, to });
  });

  const originChanged = input.origin !== mapped.origin;
  const pathnameChanged = input.pathname !== mapped.pathname;

  return {
    originChanged,
    pathnameChanged,
    inputOrigin: input.origin,
    mappedOrigin: mapped.origin,
    inputPathname: input.pathname,
    mappedPathname: mapped.pathname,
    queryChanges,
    isIdentical:
      !originChanged &&
      !pathnameChanged &&
      queryChanges.length === 0 &&
      inputUrl === mappedUrl,
  };
};
